import {useCallback, useMemo, useState} from 'react';
import {Select} from '@gravity-ui/uikit';
import {useFunds} from '../../lib';
import {FundModel, FundWithMetaModel} from '../../model';

interface SelectFundOption {
  value: string;
  content: string;
}

interface SelectFundProps {
  value: string;
  onUpdate: (value: string) => void;
  error?: string | false;
  placeholder?: string;
  size?: 's' | 'm' | 'l' | 'xl';
}

export const SelectFund = ({
  value,
  onUpdate,
  error,
  placeholder = 'Выберите фонд',
  size = 'm',
}: SelectFundProps) => {
  const {getInfiniteFunds, updateFilter} = useFunds();
  const {data, isLoading} = getInfiniteFunds;

  // Хранит отображаемое имя выбранного Фонда, чтобы Select показывал его
  // даже когда поисковый запрос исключил этот Фонд из текущих результатов
  const [selectedOption, setSelectedOption] = useState<SelectFundOption | null>(null);

  const fundOptions = useMemo<SelectFundOption[]>(() => {
    return (
      data?.pages.flatMap((page: FundWithMetaModel) =>
        page.items.map((fund: FundModel) => ({
          value: fund.id,
          content: fund.name,
        })),
      ) ?? []
    );
  }, [data]);

  const optionsWithSelected = useMemo<SelectFundOption[]>(() => {
    // Подставляем сохранённую опцию только если она соответствует текущему value
    // и отсутствует в текущих результатах поиска. Это не даёт устаревшему Фонду
    // протечь в форму, value которой очищено или указывает на другой Фонд.
    const shouldPrependSelected =
      selectedOption &&
      value === selectedOption.value &&
      !fundOptions.some((o) => o.value === selectedOption.value);
    return shouldPrependSelected ? [selectedOption, ...fundOptions] : fundOptions;
  }, [fundOptions, selectedOption, value]);

  const handleUpdate = useCallback(
    (newValue: string[]) => {
      const id = newValue[0] ?? '';
      const opt = fundOptions.find((o) => o.value === id);
      if (opt) setSelectedOption(opt);
      onUpdate(id);
    },
    [fundOptions, onUpdate],
  );

  const handleFilterChange = useCallback(
    (filter: string) => {
      updateFilter({query: filter});
    },
    [updateFilter],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) updateFilter({query: ''});
    },
    [updateFilter],
  );

  return (
    <Select
      size={size}
      options={optionsWithSelected}
      onUpdate={handleUpdate}
      value={value ? [value] : []}
      loading={isLoading}
      placeholder={placeholder}
      errorPlacement="inside"
      errorMessage={error || undefined}
      validationState={error ? 'invalid' : undefined}
      filterable
      filterPlaceholder="Найти фонд"
      onFilterChange={handleFilterChange}
      filterOption={() => true}
      renderEmptyOptions={() => <span>Фонд не найден</span>}
      onOpenChange={handleOpenChange}
      width="max"
    />
  );
};
